import {
  AlertBanner,
  Badge,
  Checkbox,
  ErrorBoundary,
  InputField,
  Label,
  PasswordInput,
  PopupModal,
  PromoCodeInput,
  RadioField,
  RadioGroup,
  Select,
  Textarea,
  Toggle,
  VerificationCodeInput,
} from '@/shared/ui'
import { TogglePreview, VerificationCodePreview } from './catalog-previews'
import type { CatalogComponentEntry } from './component-registry.types'
import { buttonVariantEntries } from './component-registry.shared.buttons'
import { contentGroupEntries } from './component-registry.shared.content-groups'
import { iconEntries } from './component-registry.shared.icons'
import { textLinkEntries } from './component-registry.shared.text-links'
import { PopupModalCatalogPreview } from './popup-modal-preview'

const sharedEntries: CatalogComponentEntry[] = [
  {
    name: 'Badge',
    path: 'shared/ui/badge',
    layer: 'shared',
    description: 'Status or tag indicator.',
    component: Badge,
    defaultProps: { children: 'Badge', variant: 'success' },
  },
  {
    name: 'AlertBanner',
    path: 'shared/ui/alert-banner',
    layer: 'shared',
    description: 'Inline feedback banner with optional action.',
    component: AlertBanner,
    defaultProps: {
      variant: 'success',
      title: 'Success',
      message: 'Your changes were saved.',
    },
  },
  {
    name: 'Popup Modal',
    path: 'shared/ui/popup-modal',
    layer: 'shared',
    description: 'Popup modal with desktop/mobile variants and overlay.',
    component: PopupModal,
    render: () => <PopupModalCatalogPreview />,
  },
  {
    name: 'VerificationCodeInput',
    path: 'shared/ui/verification-code-input',
    layer: 'shared',
    description: 'Multi-cell OTP input for verification codes.',
    component: VerificationCodeInput,
    render: () => <VerificationCodePreview />,
  },
  {
    name: 'Toggle',
    path: 'shared/ui/toggle',
    layer: 'shared',
    description: 'Binary on/off switch.',
    component: Toggle,
    render: () => <TogglePreview />,
  },
  {
    name: 'Checkbox',
    path: 'shared/ui/checkbox',
    layer: 'shared',
    description: 'Single checkbox input.',
    component: Checkbox,
    render: () => (
      <div className="flex flex-col gap-2">
        <Checkbox defaultChecked>Accept terms</Checkbox>
        <Checkbox defaultChecked aria-label="Checked" />
      </div>
    ),
  },
  {
    name: 'ErrorBoundary',
    path: 'shared/ui/error-boundary',
    layer: 'shared',
    description: 'Route error boundary view (requires router context).',
    component: ErrorBoundary,
    render: () => (
      <div className="rounded-md border border-dashed border-border bg-muted/30 p-3 text-sm text-muted-foreground">
        ErrorBoundary requires a router context to render.
      </div>
    ),
  },
  {
    name: 'Input Field',
    path: 'shared/ui/input-field',
    layer: 'shared',
    description: 'Labeled input field with helper text.',
    component: InputField,
    render: () => <InputField label="Label" placeholder="Type here" helperText="Helper text" />,
  },
  {
    name: 'Input / Password',
    path: 'shared/ui/input-field',
    layer: 'shared',
    description: 'Password input with visibility toggle.',
    component: PasswordInput,
    render: () => <PasswordInput label="Password" placeholder="Enter password" />,
  },
  {
    name: 'Input / Promo Code',
    path: 'shared/ui/input-field',
    layer: 'shared',
    description: 'Promo code input variant.',
    component: PromoCodeInput,
    render: () => <PromoCodeInput label="Promo code" placeholder="Enter code" />,
  },
  {
    name: 'Label',
    path: 'shared/ui/label',
    layer: 'shared',
    description: 'Form label text.',
    component: Label,
    defaultProps: { children: 'Label', htmlFor: 'catalog-input' },
  },
  {
    name: 'RadioField',
    path: 'shared/ui/radio-group',
    layer: 'shared',
    description: 'Tokenized radio field with label and icon.',
    component: RadioField,
    render: () => (
      <RadioGroup>
        <RadioField id="catalog-radio-a" name="catalog-radio" defaultChecked label="Option A" />
        <RadioField id="catalog-radio-b" name="catalog-radio" label="Option B" />
      </RadioGroup>
    ),
  },
  {
    name: 'Select',
    path: 'shared/ui/select',
    layer: 'shared',
    description: 'Native select control.',
    component: Select,
    render: () => (
      <Select defaultValue="option-1">
        <option value="option-1">Option 1</option>
        <option value="option-2">Option 2</option>
      </Select>
    ),
  },
  {
    name: 'Textarea',
    path: 'shared/ui/textarea',
    layer: 'shared',
    description: 'Multiline text input.',
    component: Textarea,
    defaultProps: { placeholder: 'Write a note' },
  },
  {
    name: 'Textarea (thin)',
    path: 'shared/ui/textarea',
    layer: 'shared',
    description: 'Multiline text input (thin variant).',
    component: Textarea,
    render: () => <Textarea variant="thin" placeholder="Write a note" />,
  },
]

export const sharedRegistryEntries: CatalogComponentEntry[] = [
  ...buttonVariantEntries,
  ...contentGroupEntries,
  ...iconEntries,
  ...textLinkEntries,
  ...sharedEntries,
]
